import { apiFetch } from '@/services/apiFetch'

export interface ChatHistoryItem {
  role: 'user' | 'assistant'
  content: string
}

export interface MaterialProposalSummary {
  id: string
  type: 'CREATE_MATERIALS' | 'QUOTE_INTAKE' | 'COMBINED'
  title: string
  lines?: MaterialProposalLine[]
  data?: any
}

export interface ChatResponse {
  ok: boolean
  data?: {
    message: string
    toolsCalled?: string[]
    proposal?: MaterialProposalSummary | null
  }
  error?: {
    code: string
    message: string
    status: number
  }
}

export interface MaterialProposalLine {
  id?: string
  name: string
  code?: string | null
  spec?: string | null
  quy_cach?: string | null
  uom_default: string
  group_code?: string | null
  kind: 'RAW' | 'AUX'
  selected?: boolean
  is_duplicate?: boolean
  duplicate_reason?: string | null
  duplicate_material?: { id: string; code: string; name: string } | null
  existing_material?: { id: string; code: string; name: string; uom_default?: string | null } | null
  errors?: string[]
  warnings?: string[]
  notes?: string | null
  map_to_quote_line_idx?: number | null
}

export interface ProposalDetailResponse {
  ok: boolean
  data: {
    id: string
    type: 'CREATE_MATERIALS' | 'QUOTE_INTAKE' | 'COMBINED'
    title: string
    status: 'PENDING' | 'CONFIRMED' | 'REJECTED'
    warnings?: string[]
    data: {
      materials?: MaterialProposalLine[]
      quote?: any
    }
    result?: {
      created_materials?: Array<{ id: string; code: string; name: string }>
      quote_id?: string
    }
  }
}

export async function getMaterialProposal(id: string): Promise<ProposalDetailResponse> {
  const res = await apiFetch(`/api/ai/proposals/${id}`)
  if (!res.ok) throw new Error('Không thể tải đề xuất tạo vật tư.')
  return res.json()
}

export async function confirmMaterialProposal(
  id: string,
  materials: MaterialProposalLine[],
  options?: { combined?: boolean; quote?: any },
) {
  const res = await apiFetch(`/api/ai/proposals/${id}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ materials, ...options }),
  })
  const body = await res.json()
  if (!res.ok) throw new Error(body?.error?.message || 'Không thể xác nhận đề xuất tạo vật tư.')
  return body as {
    ok: boolean
    result: {
      created_materials: Array<{ id: string; code: string; name: string }>
      quote_id?: string
    }
  }
}

export interface SuggestedMaterialDto {
  id: string
  code: string
  name: string
  uom_default: string | null
  score: number
  reason: string
}

export interface ExtractedQuoteLineDto {
  id: string
  supplier_item_name: string
  spec: string | null
  uom: string | null
  quantity: number | null
  unit_price: number | null
  note: string | null
  source_ref: string | null
  has_warning?: boolean
  warnings?: string[]
  item_id?: string | null
  suggested_materials?: SuggestedMaterialDto[]
}

export interface ExtractQuoteResultData {
  supplier_name: string | null
  quote_date: string | null
  vat_rate: number | null
  currency: string | null
  lines: ExtractedQuoteLineDto[]
  matched_supplier?: {
    id: string
    code: string
    name: string
  } | null
  raw_text: string
  source_type: string
  original_file_name?: string
}

export interface FileMetadataDto {
  original_file_name: string
  content_type: string
  bytes_base64?: string
  size_bytes: number
}

export interface ExtractQuoteResponse {
  ok: boolean
  data?: ExtractQuoteResultData
  file_metadata?: FileMetadataDto | null
  error?: {
    code: string
    message: string
    status: number
  }
}

export interface ConfirmQuotePayload {
  supplier_id: string
  title?: string
  quote_date?: string | null
  vat_rate?: number | null
  currency?: string
  notes?: string | null
  file?: {
    original_file_name: string
    content_type?: string | null
    bytes_base64: string
  } | null
  lines: Array<{
    supplier_item_name: string
    spec?: string | null
    uom: string
    unit_price: number
    item_id?: string | null
    note?: string | null
    source_ref?: string | null
    quantity?: number | null
  }>
}

export async function sendChatMessage(
  message: string,
  history: ChatHistoryItem[] = [],
  images?: Array<{ name: string; type: string; base64: string }>,
): Promise<ChatResponse> {
  const res = await apiFetch('/api/ai/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, history, images }),
  })

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const errorMsg =
      body?.error?.message ||
      body?.message ||
      `Lỗi từ máy chủ ERP (${res.status})`
    const errorCode = body?.error?.code || 'ERROR'
    return {
      ok: false,
      error: { code: errorCode, message: errorMsg, status: res.status },
    }
  }

  return body as ChatResponse
}

export async function extractQuoteFromFile(file: File): Promise<ExtractQuoteResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const res = await apiFetch('/api/ai/extract-quote', {
    method: 'POST',
    body: formData,
  })

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const errorMsg =
      body?.error?.message ||
      body?.message ||
      `Lỗi trích xuất file (${res.status})`
    const errorCode = body?.error?.code || 'ERROR'
    return {
      ok: false,
      error: { code: errorCode, message: errorMsg, status: res.status },
    }
  }

  return body as ExtractQuoteResponse
}

export async function extractQuoteFromText(text: string): Promise<ExtractQuoteResponse> {
  const res = await apiFetch('/api/ai/extract-quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  })

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const errorMsg =
      body?.error?.message ||
      body?.message ||
      `Lỗi phân tích văn bản (${res.status})`
    const errorCode = body?.error?.code || 'ERROR'
    return {
      ok: false,
      error: { code: errorCode, message: errorMsg, status: res.status },
    }
  }

  return body as ExtractQuoteResponse
}

export async function extractQuoteFromJson(json: object): Promise<ExtractQuoteResponse> {
  const res = await apiFetch('/api/ai/extract-quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ json }),
  })

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const errorMsg =
      body?.error?.message ||
      body?.message ||
      `Lỗi nạp dữ liệu JSON (${res.status})`
    const errorCode = body?.error?.code || 'ERROR'
    return {
      ok: false,
      error: { code: errorCode, message: errorMsg, status: res.status },
    }
  }

  return body as ExtractQuoteResponse
}

export async function confirmAiQuoteApi(payload: ConfirmQuotePayload) {
  const res = await apiFetch('/api/ai/confirm-quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const errorMsg =
      body?.error?.message ||
      body?.message ||
      `Lỗi xác nhận nhập báo giá (${res.status})`
    const errorCode = body?.error?.code || 'ERROR'
    throw new Error(`[${errorCode}] ${errorMsg}`)
  }

  return body
}
